import { createContext, useContext } from 'react';

import type { SupportedLocale } from '@/utils/i18n/supportedLocales';

/**
 * Shares the OS/browser locale resolved by `useOsLocale()` so the ~1,100
 * `useTranslations()` consumers (and `TranslationsProvider`) read one computed
 * value via context instead of each re-running `useOsLocale()` — a feature-flag
 * hook plus an `Intl.Locale` mapping — on every mount.
 *
 * SCR-5924: the per-call-site `useOsLocale()` fan-out introduced by the APP-7834
 * OS-locale backport taxed translation-dense subtrees (notably the Note pane) on
 * mount, causing a ~39% `tab_switch` regression. Hoisting the single expensive
 * computation to the root `HeidiIntlProvider` removes the fan-out while
 * preserving behaviour (the value is identical for all consumers at a given
 * render).
 *
 * The context always carries the raw OS/browser locale resolved by
 * `useOsLocale()` (or `undefined` when `navigator.language` is unavailable or
 * unsupported) — it is not pre-gated. The fallback gate
 * (`isTauri() || !router.locale || router.locale === 'default'` — the
 * `!router.locale` arm covers on-prem, where the build strips the i18n config
 * so `router.locale` is `undefined`) is applied downstream in
 * `useTranslations()`/`TranslationsProvider`, not here. The provider (in
 * `./os-locale-context`) is mounted by `HeidiIntlProvider`, which wraps every
 * page in `_app.tsx`, so every `useTranslations()` consumer is under it.
 */
export const OsLocaleContext = createContext<SupportedLocale | undefined>(undefined);

/** Reads the hoisted OS/browser locale. Must be used under `HeidiIntlProvider`. */
export const useOsLocaleValue = (): SupportedLocale | undefined =>
  useContext(OsLocaleContext);

/**
 * Shares the `release-scribe-enable-rtl` flag the same way, for the same
 * reason: `useIsScribeEnableRtlEnabled()` is read by `useTranslations()` and
 * `useQueryUserLanguage()` (so twice per translation consumer), and the SDK
 * flag hook registers its own event handlers per instance — ~40% of all flag
 * reads on the session page were this one value. `HeidiIntlProvider` reads it
 * once (`useScribeEnableRtlFlagSource`) and publishes it here; `undefined` is
 * only the default outside any provider, where the getter reads the client.
 */
export const RtlLocaleFlagContext = createContext<boolean | undefined>(undefined);
